import type { Phase } from "../hooks/useCycles";

type FertilityMetricSource = Pick<
  NonNullable<Phase["fertilityMetrics"]>[number],
  "type" | "value"
>;
type TestResultSource = Pick<
  NonNullable<Phase["testResults"]>[number],
  "name" | "patientValue"
>;
type TriggerShotSource = Pick<NonNullable<Phase["triggerShot"]>, "date">;

export type PhaseStepCompletionSource = {
  endDate?: Phase["endDate"];
  fertilityMetrics?: FertilityMetricSource[] | null;
  testResults?: TestResultSource[] | null;
  triggerShot?: TriggerShotSource | null;
};

type StepIdSource = string | { id: string };

/**
 * Apply the API-backed completion rules shared by the treatment hub and the
 * adaptive cycle header. A phase can back more than one patient-facing step,
 * so completion is not always equivalent to phase.endDate.
 */
export function isCycleStepComplete(
  stepId: string,
  stepIds: readonly StepIdSource[],
  phase: PhaseStepCompletionSource | null | undefined,
  todayIso: string,
): boolean {
  if (!phase) return false;

  if (stepId === "trigger_shot") {
    const triggerDate = phase.triggerShot?.date;
    return !!triggerDate && triggerDate <= todayIso;
  }

  if (stepId === "pgt_testing") {
    const pgt = phase.testResults?.find(
      (result) => result.name?.toLowerCase() === "pgt",
    );
    return !!pgt && pgt.patientValue !== "pending";
  }

  const hasEggRetrievalStep = stepIds.some((step) =>
    typeof step === "string"
      ? step === "egg_retrieval"
      : step.id === "egg_retrieval",
  );
  if (stepId === "freezing" && hasEggRetrievalStep) {
    return (
      phase.fertilityMetrics?.some(
        (metric) =>
          (metric.type === "m2_count" || metric.type === "frozen_embryo") &&
          metric.value != null,
      ) ?? false
    );
  }

  return !!phase.endDate;
}
